import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { map, Observable } from 'rxjs';
import { GET_ALL_STORIES, GET_STORY } from '../store/entities/stories-management/stories.graphql';
import { content } from './svg/icons/content_management';
import { uniqueId } from 'lodash';
import { GET_STORY_BY_UUID } from '../store/entities/epaper-management/epaper.graphql';



export interface StorySlide {
  id: number;
  image: string;
  text: string;
  headline?: string;
  duration: number;
}

export interface Story {
  id: number;
  uniqueId: string;
  title: string;
  subtitle: string;
  author: string;
  readTime: string;
  thumbnail: string;
  content: string;
  category: string;
  isViewed: boolean;
  timestamp: string;
  slides: StorySlide[];
}

@Injectable({
  providedIn: 'root',
})
export class StoriesDataService {

  constructor(private apollo: Apollo) {}

  getStories(filtering?: any): Observable<Story[]> {
    return this.apollo
      .watchQuery<any>({
        query: GET_STORY,
        variables: {
          filtering,
        },
      })
      .valueChanges.pipe(
        map(({ data }) => this.mapApiStoriesToUiStories(data.getAllStories.data))
      );
  }

  /**
   * Fetches a single story by its ID.
   *
   * @param id The ID of the story to fetch.
   * @returns An Observable of the story, or undefined if not found.
   *
   * @remarks This is a temporary solution that filters all stories on the client-side.
   * A dedicated backend query (e.g., getStoryById) should be implemented for better performance.
   */
  getStoryById(uuid: string): Observable<Story | undefined> {
  return this.apollo
    .query<any>({
      query: GET_STORY_BY_UUID,
      variables: {
        uuid,
      },
      fetchPolicy: 'network-only', // always get fresh story
    })
    .pipe(
      map(({ data }) => {
        const item = data?.getStoryByUuid?.data;
        if (!item) return undefined;

        return this.mapSingleApiStoryToUiStory(item);
      })
    );
}


private mapSingleApiStoryToUiStory(item: any): Story {
  return {
    id: item.id,
    uniqueId: item.uuid,
    title: item.headline || 'Story',
    subtitle: item.subheadline || '',
    thumbnail: item.filePath,
    content: item.content || '',
    author: item.author || '',
    readTime: '4 min read',
    category: 'News',
    isViewed: false,
    timestamp: 'Just now',
    slides: [
      {
        id: 1,
        image: item.filePath,
        headline: item.headline,
        text: item.content || item.subheadline || '',
        duration: 5000,
      },
    ],
  };
}


  /**
   * Maps backend stories → UI stories
   */
  private mapApiStoriesToUiStories(apiStories: any[]): Story[] {
    return apiStories.map((item) => ({
      id: item.id,
      uniqueId: item.uuid,
      title: item.headline || 'Story',
      subtitle: item.subheadline || '',
      thumbnail: item.filePath,
      content: item.content || 'Content',
      author: item.author || '',
      readTime: '4 min read',
      category: 'News', // or derive from backend later
      isViewed: false,
      timestamp: 'Just now', // replace with backend date when available
      slides: [
        {
          id: 1,
          image: item.filePath,
          headline: item.headline,
          text: item.content || item.subheadline || '',
          duration: 5000,
        },
      ],
    }));
  }

  /**
   * Handles filePath → full URL
   */
  // private resolveImage(filePath: string): string {
  //   if (!filePath) {
  //     return 'assets/images/placeholder.jpg';
  //   }
  //   return `https://epaper.all.tz/backend/uploads/media/$caption{filePath}`;
  // }
}